import cn from 'classnames'
import * as React from 'react'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useSessionStorage } from 'react-use'
import NextStepButton from '@/scopes/checkout/components/CheckoutNextStep/NextStepButton'
import { useSendSubmitEvent } from '@/scopes/checkout/domain/analytics/useSendSubmitEvent'
import { useCheckoutContext } from '@/scopes/checkout/domain/CheckoutContext'
import { buildUrlWithCurrentParams } from '@/scopes/checkout/domain/helpers/BuildUrlWithCurrentParams'
import { useBookingDraftQuery, useCheckoutSid, useNextStepNotification, useUnselectedServices } from '@/scopes/checkout/domain/hooks'
import { ServiceType } from '@/scopes/checkout/domain/types'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { scrollToElement } from '@/scopes/common/infrastructure/dom/scrollToElement'
import { createAncillariesClickedEvent } from '../../domain/analytics'
import { NEXT_STEP_ATTRIBUTE } from '../Layout/useSidebarBottomOffset'
import styles from './CheckoutNextStep.module.css'
import { OptionalAncillariesWindow } from './OptionalAncillariesWindow'

const NAVIGATION_DELAY_MS = 200

interface CheckoutNextStepProps {
  /** Set to true when the next action should push checkout submit analytics before navigating. */
  sendSubmitEvent?: boolean
  /** Set to true to notify the backend of the transition to the next step before navigating. */
  notifyNextStep?: boolean
}

export function CheckoutNextStep({ sendSubmitEvent = false, notifyNextStep = false }: CheckoutNextStepProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { isLoading, data } = useBookingDraftQuery()
  const { unselectedServiceTypes } = useUnselectedServices()
  const sid = useCheckoutSid()
  const { step } = useCheckoutContext()
  const { notifyNextStep: triggerNextStepNotification } = useNextStepNotification()
  const { sendSubmitEvent: triggerSubmitEvent } = useSendSubmitEvent()
  const { sendEvent } = useAnalytics()

  const { trigger } = useFormContext() ?? {}

  const [optionalAncillariesModalIsShown, setOptionalAncillariesModalIsShown] = useSessionStorage(
    `${sid}-next-step-optional-ancillaries-modal-is-shown`,
    false,
  )
  const nextStep = data?.navigation?.nextStep
  if (!nextStep)
    return null

  const onClose = () => {
    setIsModalOpen(false)
    sendEvent(
      createAncillariesClickedEvent({
        result: 'back to ancillaries',
        description: 'optional ancillaries popup',
        element: 'element clicked',
        interaction: true,
      }),
    )
  }

  const goToNextStep = async () => {
    if (notifyNextStep && step) {
      triggerNextStepNotification(step + 1)
    }

    if (sendSubmitEvent) {
      triggerSubmitEvent()
      await new Promise(resolve => setTimeout(resolve, NAVIGATION_DELAY_MS))
    }

    const nextStepUrl = nextStep.url

    if (isModalOpen) {
      onClose()
    }

    window.location.href = buildUrlWithCurrentParams(nextStepUrl)
  }

  const getIfCarRentalFilteringIsValid = async () => {
    if (trigger) {
      return await trigger()
    }
    return true
  }

  const openModal = async () => {
    const isCarRentalFilteringValid = await getIfCarRentalFilteringIsValid()

    if (!isCarRentalFilteringValid) {
      scrollToElement(`#${ServiceType.CarRentalFiltering} [aria-required="true"][aria-checked="false"]`)
      sendEvent(
        createAncillariesClickedEvent({
          result: 'validation error',
          description: 'scroll to validation errors on car rental service',
          element: 'element visible',
          interaction: false,
        }),
      )
      return
    }

    if (optionalAncillariesModalIsShown || !unselectedServiceTypes.length) {
      void goToNextStep()
      return
    }
    if (!optionalAncillariesModalIsShown) {
      setIsModalOpen(true)
      setOptionalAncillariesModalIsShown(true)
      sendEvent(
        createAncillariesClickedEvent({
          result: 'pop-up shown',
          description: 'optional ancillaries popup',
          element: 'element visible',
          interaction: false,
        }),
      )
    }
  }

  return (
    <div {...{ [NEXT_STEP_ATTRIBUTE]: '' }} className={cn(styles.wrapper)}>
      <NextStepButton onClick={openModal} disabled={isLoading} text={nextStep.text} />
      <OptionalAncillariesWindow
        open={isModalOpen}
        ancillaries={unselectedServiceTypes}
        onClose={onClose}
        goToNextStep={goToNextStep}
      />
    </div>
  )
}
